/**
 * OnboardingOptional.jsx — v213.5 (C-A-OB-8)
 *
 * Screen 7 of 7. Three optional sections — Medical history, Family
 * history, Lifestyle — each in a card that expands inline when "Add" is
 * tapped.
 *
 * Data shapes match Profile screens exactly so onboarding writes can be
 * read by the Profile editor and vice versa:
 *
 *   medicalHistory.conditions   : [{ id, name, year, custom? }]
 *   medicalHistory.surgeries    : [{ id, name, year, custom? }]
 *   familyHistory.conditions    : [{ id, name, mother, father, sibling, grandparent }]
 *   familyHistory.otherConditions: same shape
 *   socialLifestyle             : { smoking: 'Yes'|'No'|'Former',
 *                                   smokingFrequency, alcohol,
 *                                   alcoholFrequency, cannabis,
 *                                   cannabisFrequency, cannabisMethod,
 *                                   exercise, occupation }
 *
 * Onboarding offers the most common conditions inline; the patient can
 * refine details (year, frequency, etc.) in their Profile later.
 */
import { useState } from 'react'
import { ClipboardList, Users, HeartPulse, X } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useOnboardingStore } from '../../stores'
import { MEDICAL_CONDITIONS, FAMILY_CONDITIONS } from '../../data/medicalLists'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  FilledBanner,
  PrimaryButton,
  SecondaryButton,
  Footer,
  ScrollBody,
  SegmentedSelect,
  useScrollToTop,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  TEXT_MUTED,
  TEXT_HINT,
  BORDER_LIGHT,
  PROFILE_STEPS,
} from './onboardingPrimitives'

// Derive the "common" subsets from medicalLists.js — same source the
// Profile screens use, so options are identical.
const COMMON_CONDITIONS = MEDICAL_CONDITIONS.filter((c) => c.common)
const COMMON_FAMILY = FAMILY_CONDITIONS.filter((c) => c.common)

// ---------- Multi-select chip rows ----------

function ConditionChips({ options, selected, onToggle, noneOn, onToggleNone, noneLabel }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {onToggleNone && (
        <button
          key="__none"
          onClick={onToggleNone}
          className="font-medium transition"
          style={{
            padding: '6px 12px',
            borderRadius: 999,
            fontSize: 12,
            background: noneOn ? BLUE : '#fff',
            border: `1px solid ${noneOn ? BLUE : BORDER_LIGHT}`,
            color: noneOn ? '#fff' : NAVY,
            cursor: 'pointer',
          }}
        >
          {noneLabel || 'None'}
        </button>
      )}
      {options.map((opt) => {
        const isOn = selected.some((s) => s.id === opt.id)
        return (
          <button
            key={opt.id}
            onClick={() => onToggle(opt)}
            className="font-medium transition"
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              fontSize: 12,
              background: isOn ? ICE_BLUE : '#fff',
              border: `1px solid ${isOn ? BLUE : BORDER_LIGHT}`,
              color: isOn ? BLUE : NAVY,
              cursor: 'pointer',
            }}
          >
            {opt.name}
          </button>
        )
      })}
    </div>
  )
}

// ---------- Family history relative-toggle row ----------

function FamilyRelativeToggles({ condition, onChange }) {
  const { t } = useI18n()
  const relatives = [
    { key: 'mother', label: t('onboarding.optional.mother', 'Mother') },
    { key: 'father', label: t('onboarding.optional.father', 'Father') },
    { key: 'sibling', label: t('onboarding.optional.sibling', 'Sibling') },
    { key: 'grandparent', label: t('onboarding.optional.grandparent', 'Grandparent') },
  ]
  return (
    <div className="flex flex-wrap gap-1.5" style={{ marginTop: 6 }}>
      {relatives.map((r) => {
        const isOn = !!condition[r.key]
        return (
          <button
            key={r.key}
            onClick={() => onChange({ ...condition, [r.key]: !isOn })}
            className="font-medium transition"
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 11,
              background: isOn ? BLUE : '#fff',
              border: `1px solid ${isOn ? BLUE : BORDER_LIGHT}`,
              color: isOn ? '#fff' : NAVY,
              cursor: 'pointer',
            }}
          >
            {r.label}
          </button>
        )
      })}
    </div>
  )
}

// ---------- ExpandableCard ----------

function ExpandableCard({ icon: Icon, title, desc, expanded, onToggle, hasData, children }) {
  const { t } = useI18n()
  return (
    <div
      className="rounded-[14px]"
      style={{
        background: '#fff',
        border: `1px solid ${expanded ? LIGHT_BLUE : BORDER_LIGHT}`,
        marginBottom: 8,
        overflow: 'hidden',
        transition: 'border-color 200ms',
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: 13 }}>
        <div
          className="rounded-[10px] flex items-center justify-center flex-shrink-0"
          style={{ width: 38, height: 38, background: ICE_BLUE }}
        >
          <Icon size={18} color={BLUE} />
        </div>
        <div className="flex-1">
          <div style={{ fontSize: 13.5, fontWeight: 500, color: NAVY, marginBottom: 2 }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.4 }}>{desc}</div>
        </div>
        <button
          onClick={onToggle}
          style={{
            padding: '6px 14px',
            borderRadius: 999,
            background: hasData ? '#fff' : BLUE,
            border: hasData ? `1px solid ${BLUE}` : 'none',
            color: hasData ? BLUE : '#fff',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {expanded
            ? t('common.done', 'Done')
            : hasData
              ? t('common.edit', 'Edit')
              : t('common.add', 'Add')}
        </button>
      </div>
      {expanded && (
        <div style={{ padding: '4px 13px 14px', borderTop: `1px solid ${BORDER_LIGHT}` }}>
          {children}
        </div>
      )}
    </div>
  )
}

// ---------- Screen ----------

export default function OnboardingOptional({ onBack, onFinish }) {
  const { t } = useI18n()
  const optional = useOnboardingStore((s) => s.optional)
  const autofillResult = useOnboardingStore((s) => s.autofillResult)
  const setOptional = useOnboardingStore((s) => s.setOptional)

  useScrollToTop('optional')

  const [expanded, setExpanded] = useState(null) // null | 'med' | 'fam' | 'lif'

  // -------- Medical history --------
  const medicalHistory = optional.medicalHistory || { conditions: [], surgeries: [], noConditions: false, noSurgeries: true }
  // review r1 (M2): `how` is `{ perItem: true }` from a per-item removal or edit only; the
  // "None" toggles clear the list without suppressing anything against a later upload.
  const setMedicalHistory = (patch, how) =>
    setOptional({ medicalHistory: { ...medicalHistory, ...patch } }, how)

  const medConditions = medicalHistory.conditions || []
  const medNoneOn = medicalHistory.noConditions === true && medConditions.length === 0
  const toggleMedNone = () =>
    setMedicalHistory(medNoneOn ? { noConditions: false } : { conditions: [], noConditions: true })
  const toggleMedCondition = (opt) => {
    const exists = medConditions.find((c) => c.id === opt.id)
    if (exists) {
      setMedicalHistory({
        conditions: medConditions.filter((c) => c.id !== opt.id),
        noConditions: medConditions.length === 1, // last one being removed
      }, { perItem: true })
    } else {
      setMedicalHistory({
        conditions: [...medConditions, { id: opt.id, name: opt.name, year: '' }],
        noConditions: false,
      })
    }
  }

  // -------- Family history --------
  const familyHistory = optional.familyHistory || {
    conditions: [],
    otherConditions: [],
    noConditions: false,
    unknownRelatives: { mother: false, father: false, sibling: false },
  }
  const setFamilyHistory = (patch, how) =>
    setOptional({ familyHistory: { ...familyHistory, ...patch } }, how)

  const famConditions = familyHistory.conditions || []
  // PROD-428 (b): "None" is on only while BOTH lists are empty (custom family conditions count)
  const famNoneOn = familyHistory.noConditions === true && famConditions.length === 0 && (familyHistory.otherConditions || []).length === 0
  // PROD-473 S7h: "None" clears the custom conditions too. This card never renders otherConditions,
  //   so with one there (a record's custom grandmother condition) the chip stayed off and a second
  //   tap did nothing. Like the other "None" toggles it records no removal (review r1 M2).
  const toggleFamNone = () =>
    setFamilyHistory(famNoneOn ? { noConditions: false } : { conditions: [], otherConditions: [], noConditions: true })
  const toggleFamCondition = (opt) => {
    const exists = famConditions.find((c) => c.id === opt.id)
    if (exists) {
      setFamilyHistory({ conditions: famConditions.filter((c) => c.id !== opt.id) }, { perItem: true })
    } else {
      // Default: assume the first relative the patient is thinking of is mother
      // (Profile lets them refine — onboarding offers a quick relative-toggle row).
      setFamilyHistory({
        noConditions: false,
        conditions: [
          ...famConditions,
          { id: opt.id, name: opt.name, mother: false, father: false, sibling: false, grandparent: false },
        ],
      })
    }
  }
  const updateFamCondition = (next) => {
    setFamilyHistory({
      conditions: famConditions.map((c) => (c.id === next.id ? next : c)),
    }, { perItem: true })
  }

  // -------- Social lifestyle (Profile-shaped: 'Yes' | 'No' | 'Former') --------
  const lifestyle = optional.socialLifestyle || {}
  const setLifestyle = (patch) =>
    setOptional({ socialLifestyle: { ...lifestyle, ...patch } })

  // -------- Card "hasData" indicators --------
  const hasMed = medConditions.length > 0 || (medicalHistory.surgeries || []).length > 0 || medicalHistory.noConditions === true
  const hasFam = famConditions.length > 0 || (familyHistory.otherConditions || []).length > 0 || familyHistory.noConditions === true
  const hasLif = lifestyle.smoking || lifestyle.alcohol || lifestyle.cannabis || lifestyle.exercise || lifestyle.occupation

  return (
    <OnboardingShell>
      <ScreenHeader onBack={onBack} step={4} total={PROFILE_STEPS} />
      <ScreenIntro
        eyebrow={t('onboarding.optional.eyebrow', 'Almost done')}
        title={t('onboarding.optional.title', 'A bit more (optional)')}
        dialogue={t('onboarding.optional.dialogue', "Your conditions and family history are pre-filled from your upload too — expand to confirm, or skip what you like.")}
      />

      <ScrollBody>
        {autofillResult && (
          <FilledBanner>{t('onboarding.optional.filled', 'Conditions & family history filled from your record — review & confirm')}</FilledBanner>
        )}
        <ExpandableCard
          icon={ClipboardList}
          title={t('onboarding.optional.medical', 'Medical history')}
          desc={t('onboarding.optional.medicalDesc', 'Past conditions diagnosed by a doctor')}
          expanded={expanded === 'med'}
          onToggle={() => setExpanded(expanded === 'med' ? null : 'med')}
          hasData={hasMed}
        >
          <p style={{ fontSize: 11.5, color: TEXT_MUTED, marginBottom: 10 }}>
            {t('onboarding.optional.medicalHelp', 'Tap any that apply. You can refine details (year, surgeries) later in your profile.')}
          </p>
          <ConditionChips
            options={COMMON_CONDITIONS}
            selected={medConditions}
            onToggle={toggleMedCondition}
            noneOn={medNoneOn}
            onToggleNone={toggleMedNone}
            noneLabel={t('onboarding.optional.noneApply', 'None of these')}
          />
        </ExpandableCard>

        <ExpandableCard
          icon={Users}
          title={t('onboarding.optional.family', 'Family history')}
          desc={t('onboarding.optional.familyDesc', 'Conditions in your immediate family')}
          expanded={expanded === 'fam'}
          onToggle={() => setExpanded(expanded === 'fam' ? null : 'fam')}
          hasData={hasFam}
        >
          <p style={{ fontSize: 11.5, color: TEXT_MUTED, marginBottom: 10 }}>
            {t('onboarding.optional.familyHelp', 'Conditions diagnosed in parents, siblings, or grandparents.')}
          </p>
          <ConditionChips
            options={COMMON_FAMILY}
            selected={famConditions}
            onToggle={toggleFamCondition}
            noneOn={famNoneOn}
            onToggleNone={toggleFamNone}
            noneLabel={t('onboarding.optional.noneApply', 'None of these')}
          />
          {famConditions.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 11, color: TEXT_HINT, marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}>
                {t('onboarding.optional.whichRelative', 'Who has each — tap any')}
              </p>
              {famConditions.map((c) => (
                <div key={c.id} style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: 12.5, color: NAVY, fontWeight: 500 }}>{c.name}</p>
                  <FamilyRelativeToggles condition={c} onChange={updateFamCondition} />
                </div>
              ))}
            </div>
          )}
        </ExpandableCard>

        <ExpandableCard
          icon={HeartPulse}
          title={t('onboarding.optional.lifestyle', 'Lifestyle')}
          desc={t('onboarding.optional.lifestyleDesc2', 'Tobacco, alcohol, cannabis, exercise, occupation')}
          expanded={expanded === 'lif'}
          onToggle={() => setExpanded(expanded === 'lif' ? null : 'lif')}
          hasData={hasLif}
        >
          {/* Profile uses 'Yes' | 'No' | 'Former' for smoking/alcohol/cannabis */}
          <SegmentedSelect
            label={t('onboarding.optional.smoking', 'Tobacco')}
            value={lifestyle.smoking || ''}
            onChange={(v) => setLifestyle({ smoking: v, smokingFrequency: v !== 'Yes' ? '' : lifestyle.smokingFrequency || '' })}
            required={false}
            options={[
              { value: 'Yes',    label: t('onboarding.optional.yes',    'Yes')    },
              { value: 'No',     label: t('onboarding.optional.no',     'No')     },
              { value: 'Former', label: t('onboarding.optional.former', 'Former') },
            ]}
          />
          <SegmentedSelect
            label={t('onboarding.optional.alcohol', 'Alcohol')}
            value={lifestyle.alcohol || ''}
            onChange={(v) => setLifestyle({ alcohol: v, alcoholFrequency: v !== 'Yes' ? '' : lifestyle.alcoholFrequency || '' })}
            required={false}
            options={[
              { value: 'Yes',    label: t('onboarding.optional.yes',    'Yes')    },
              { value: 'No',     label: t('onboarding.optional.no',     'No')     },
              { value: 'Former', label: t('onboarding.optional.former', 'Former') },
            ]}
          />
          <SegmentedSelect
            label={t('onboarding.optional.exercise', 'Exercise')}
            value={lifestyle.exercise || ''}
            onChange={(v) => setLifestyle({ exercise: v })}
            required={false}
            options={[
              // ★ v242.150.0 SOCIAL-EXERCISE-VOCAB-1 — the VALUES are the profile's
              //   terms; the labels stay short. These used to be 'None' /
              //   'Sometimes' / 'Regular', which match no <option> on the profile
              //   screen, so a stored answer rendered as "Select…".
              { value: 'Never',              label: t('onboarding.optional.exNone',    'None')   },
              { value: '1-2 times per week', label: t('onboarding.optional.exSome',    '1–2x/wk') },
              { value: '3-4 times per week', label: t('onboarding.optional.exRegular', '3+/wk')  },
            ]}
          />
          {/* ★ v242.150.0 SOCIAL-SIGNUP-FIELDS-1 — his ruling: ask these here, so
              the profile section can honestly report itself complete. */}
          <SegmentedSelect
            label={t('onboarding.optional.cannabis', 'Cannabis')}
            value={lifestyle.cannabis || ''}
            onChange={(v) => setLifestyle({ cannabis: v, cannabisFrequency: v !== 'Yes' ? '' : lifestyle.cannabisFrequency || '' })}
            required={false}
            options={[
              { value: 'No',     label: t('onboarding.optional.no', 'No') },
              { value: 'Yes',    label: t('onboarding.optional.yes', 'Yes') },
              { value: 'Former', label: t('onboarding.optional.former', 'Former') },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: NAVY, marginBottom: 6 }}>
              {t('onboarding.optional.occupation', 'Occupation')}
            </label>
            <input
              type="text"
              value={lifestyle.occupation || ''}
              onChange={(e) => setLifestyle({ occupation: e.target.value })}
              placeholder={t('onboarding.optional.occupationPlaceholder', 'e.g. Teacher, retired, student')}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
                       border: '1.5px solid #E2E8F0', borderRadius: 12, outline: 'none', color: NAVY }}
            />
          </div>
        </ExpandableCard>

        <p style={{ fontSize: 11.5, color: TEXT_HINT, marginTop: 14 }}>
          {t('onboarding.optional.laterHint', 'All three can be added later from your profile.')}
        </p>
      </ScrollBody>

      <Footer>
        <PrimaryButton onClick={onFinish}>
          {t('onboarding.optional.finish', 'Finish setup')}
        </PrimaryButton>
        <SecondaryButton onClick={onFinish}>
          {t('onboarding.optional.skipAll', 'Skip all and finish')}
        </SecondaryButton>
      </Footer>
    </OnboardingShell>
  )
}
